/* eslint-disable sonarjs/no-identical-functions */

import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useRouter, homeRoute } from '@src/routes'
import DnaTubeLayout from './products/DnaTubeLayout'
import {
  TubeTitleMap,
  TubeProduct,
  ModificationTypes,
  OligoNameMap,
  AntiSenseNameMap,
} from './enum'
import { useUserSession } from '@src/utils/useUserSession'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { useLabWaterParticipantId } from '@src/utils/useLabWaterParticipantId'
import { BiTubeProduct } from '../BiTube/enum'
import { BreadcrumbNavigationUrls } from '@src/routes/Configurators/utils/BreadcrumbNavigationUrlsEnum'
import { SemiConfigProductTypes } from '@src/routes/SemiConfigurators/products/SemiConfiguratorRedesign/enum'

export enum TabsEnum {
  SequenceEntry = 'sequenceEntry',
  MultipleEntry = 'multipleEntry',
}

export enum ActiveLinkType {
  SequenceEntry = 'sequenceEntry',
  ConfirmOrder = 'confirmOrder',
  MultipleEntry = 'multipleEntry',
}

export interface ListError {
  key: string
  value: string
}

//product specific sequence base values
//when there is country specific value add it here, Eg: US: {min :7, max: 120}
export const productCountrySpecificBaseValue = {
  [TubeProduct.Standard]: {
    default: {
      min: 6,
      max: 120,
    },
  },
  [TubeProduct.EasyOligo]: {
    default: {
      min: 15,
      max: 30,
    },
  },
  [TubeProduct.Rna]: {
    default: {
      min: 7,
      max: 45,
    },
  },
  [TubeProduct.PureSimple]: {
    default: {
      min: 12,
      max: 35,
    },
    JP: {
      min: 13,
      max: 35,
    },
  },
  [TubeProduct.iscaledna]: {
    default: {
      min: 10,
      max: 55,
    },
  },
  [TubeProduct.LongOligo]: {
    default: {
      min: 121,
      max: 180,
    },
  },
  [TubeProduct.Wellred]: {
    default: {
      min: 15,
      max: 30,
    },
  },
  [TubeProduct.Beacon]: {
    default: {
      min: 7,
      max: 50,
    },
  },
  [TubeProduct.Probe]: {
    default: {
      min: 7,
      max: 50,
    },
    internalquench: {
      min: 10,
      max: 50,
    },
    dualquench: {
      min: 10,
      max: 50,
    },
  },
  [BiTubeProduct.LightCycler]: {
    default: {
      min: 7,
      max: 40,
    },
  },
  [BiTubeProduct.Duplex]: {
    default: {
      min: 19,
      max: 29,
      overHang: 2,
    },
  },
  [BiTubeProduct.FastDuplex]: {
    default: {
      min: 19,
      max: 23,
    },
  },
  [BiTubeProduct.mirna]: {
    default: {
      min: 19,
      max: 27,
    },
  },
}

export interface InsertModificationArgs {
  value: string
  resetMod?: () => void
  isAntiSense?: boolean
  triggerValidate?: boolean
}

export interface SingleTubeFormValues {
  additionalServices?: string[]
  amount?: string
  baseCount?: number
  brand?: string
  cartId?: string
  comments?: string
  concentration?: string
  currency?: string
  currencySymbol?: string
  displayPageError: boolean
  enabledAdditionalServices?: string[]
  enabledConcentrationData?: string[]
  enabledFiveModifications?: string[]
  enabledFormatData?: string[]
  enabledInternalModifications?: string[]
  enabledPurificationData?: string[]
  enabledScaleData?: string[]
  enabledThreeModifications?: string[]
  enabledAmountData?: string[]
  epsilon?: number
  fiveMod?: string
  fiveModCategory?: string
  format?: string
  gcContent?: string
  insertAsterisk?: boolean
  internalMod?: string
  internalModCategory?: string
  isOverridden?: boolean
  itemId?: string
  listErrors: ListError[]
  listWarning?: ListError[]
  listMinimumYield?: number
  listNumberOfTubes?: string[]
  material?: string
  meltingTemperature?: string
  modification?: string
  molecularWeight?: string
  name: string
  oligoId?: number
  otherValue?: string
  price?: string
  primerDimer?: string
  probeType?: string
  purification?: string
  purificationWarning?: string[]
  scale?: string
  secondaryStructure?: string
  sequence: string
  sequenceWarning?: string[]
  threeMod?: string
  threeModCategory?: string
  tubes?: string
  flag?: number
  'five-reporter-three-quencher'?: string
  isLNABaseEnable?: boolean
  antisenceName?: string
  antisenceSequence?: string
  antisenceFiveMod?: string
  antisenceThreeMod?: string
  antisenceBaseCount?: number
  senseNameCheck?: Boolean
  antisenseBaseCount?: string
  antisenseMolecularWeight?: string
  antisensePrimerDimer?: string
  antisenseMeltingTemperature?: string
  antisenseGcContent?: string
  antisenseSecondaryStructure?: string
  customerOverhang?: string
  customerAntisenseOverHang?: string
  autoGenerateOligo?: boolean
}
// Type of values of the outer formik form
export interface TubesFormValues {
  activeOligoIndex: number
  activeTab?: number
  applyToNextOligos: boolean
  copiedEntry?: string
  copypasteLoading?: boolean
  displayPageError: boolean
  isoCode?: boolean
  oligoList: SingleTubeFormValues[]
  showCautionPopup?: boolean
  validationMessage?: string[]
  validUploadTemplate?: boolean
  hasTriedNextStep?: boolean
  activeValidationButton?: string
  loading?: boolean
  antisenseActiveTab?: number
}

//type of product sidebar item
export interface SideBarItem {
  activeLink: boolean
  label: string
  linkDisable: boolean
  url: string
}
//type of product breadcrumb item
export interface BreadCrumbItem {
  url?: string
  label: React.ReactNode
}
// Used for checking the new design is enabled for this product or not
export const checkForRedesignedProduct = (
  product: string,
  isTubesRedesignFeatureFlagEnabled: boolean,
  isTubesRedesignRnaRnaEasyOligoPureAndSimple: boolean,
  isTubesRedesignIScaleLongWellRed: boolean,
  isTubesRedesignBeaconAndDualLabeled: boolean
): boolean => {
  const tubesRedesignProducts: string[] = [TubeProduct.Standard]
  const rnaRedesignProducts: string[] = [
    TubeProduct.Rna,
    TubeProduct.EasyOligo,
    TubeProduct.PureSimple,
  ]

  const iscaleLongWellRedesignProducts: string[] = [
    TubeProduct.iscaledna,
    TubeProduct.LongOligo,
    TubeProduct.Wellred,
  ]

  const beaconAndDualLabeledProducts: string[] = [
    TubeProduct.Beacon,
    TubeProduct.Probe,
  ]

  return (
    (tubesRedesignProducts.includes(product) &&
      isTubesRedesignFeatureFlagEnabled) ||
    (rnaRedesignProducts.includes(product) &&
      isTubesRedesignRnaRnaEasyOligoPureAndSimple) ||
    (iscaleLongWellRedesignProducts.includes(product) &&
      isTubesRedesignIScaleLongWellRed) ||
    (beaconAndDualLabeledProducts.includes(product) &&
      isTubesRedesignBeaconAndDualLabeled)
  )
}
//get the product value from the query string
export const useProductFromQueryParam = (): string => {
  const router = useRouter()
  return Array.isArray(router.query?.product)
    ? router.query?.product[0]
    : router.query?.product || ''
}

export interface ProductInputType {
  locale?: string
  productType?: string
  isCSR?: boolean
  isCSRSupervisor?: boolean
  isRegUser?: boolean
  countryCode?: string
  participantId?: string
}

export const useProductInput = (): ProductInputType => {
  const { userSession } = useUserSession()
  const { isCustomerServiceRep, isCustomProductCSR, userIsLoggedIn } =
    useCurrentUser()
  const participantId = useLabWaterParticipantId()
  return {
    locale: `${userSession.language}_${userSession.country}`,
    productType: useProductFromQueryParam(),
    isCSR: isCustomerServiceRep,
    isCSRSupervisor: isCustomProductCSR,
    isRegUser: userIsLoggedIn,
    countryCode: userSession.country,
    participantId,
  }
}

// Form initial values for the outer formik form
export const getInitialValuesBasedOnProduct = (product: string) => {
  const commonOligoValues = {
    applyToNextOligos: false,
    concentration: '',
    displayPageError: false,
    enabledConcentrationData: [],
    enabledFormatData: [],
    enabledPurificationData: [],
    format: 'DRY',
    listErrors: [],
    ListWarning: [],
    name: '',
    purification: '',
    sequence: '',
    tubes: '1',
  }
  const commonBiTubeOligoValues = {
    antisenceThreeMod: '',
    antisenceFiveMod: '',
    amount: '',
    format: 'DRY',
    tubes: '1',
    antisenceName: '',
    antisenceSequence: '',
    customerOverhang: '',
    customerAntisenseOverHang: '',
    senseNameCheck: false,
    autoGenerateOligo: false,
  }
  const standardValues = {
    enabledFiveModifications: [],
    enabledInternalModifications: [],
    enabledScaleData: [],
    enabledThreeModifications: [],
    fiveMod: '',
    fiveModCategory: '',
    insertAsterisk: false,
    internalMod: '',
    internalModCategory: '',
    listNumberOfTubes: ['1'],
    scale: '',
    threeMod: '',
    threeModCategory: '',
    isLNABaseEnable: true,
  }
  const productWideValues = {
    applyToNextOligos: false,
    displayPageError: false,
    activeOligoIndex: 0,
    activeTab: 0,
    antisenseActiveTab: 0,
  }
  // Rna & Aqua are added temporarily for handling the compilation error for switch
  switch (product) {
    case TubeProduct.Standard:
    case TubeProduct.Rna:
    case TubeProduct.Aqua:
    case TubeProduct.EasyOligo:
    // TODO: Check if these are the required values needed for the tubes below
    case TubeProduct.LongOligo:
    case TubeProduct.Wellred:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            ...standardValues,
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case TubeProduct.Beacon:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            purification: 'HPLC',
            ...standardValues,
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case TubeProduct.PureSimple:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            enabledScaleData: [],
            scale: '',
            amount: '',
          },
        ],
        ...productWideValues,
      }
    case TubeProduct.iscaledna:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            enabledFiveModifications: [],
            enabledInternalModifications: [],
            insertAsterisk: false,
            internalMod: '',
            listNumberOfTubes: ['1'],
            threeMod: '',
            flag: 0,
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case TubeProduct.Probe:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            purification: 'HPLC',
            ...standardValues,
            enabledAmountData: [],
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case BiTubeProduct.LightCycler:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            ...standardValues,
            ...commonBiTubeOligoValues,
            threeMod: '[LCFLC]',
            antisenceThreeMod: '[Phos]',
            purification: 'HPLC',
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case BiTubeProduct.Duplex:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            ...standardValues,
            ...commonBiTubeOligoValues,
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case BiTubeProduct.mirna:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            ...standardValues,
            ...commonBiTubeOligoValues,
            customerOverhang: '[dT][dT]',
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    case BiTubeProduct.FastDuplex:
      return {
        oligoList: [
          {
            ...commonOligoValues,
            ...standardValues,
            ...commonBiTubeOligoValues,
            format: 'LIQUID',
          },
        ],
        ...productWideValues,
        activeValidationButton: '',
      }
    default:
      return {
        oligoList: [
          {
            name: '',
            sequence: '',
            listNumberOfTubes: ['1'],
            applyToNextOligos: false,
            displayPageError: false,
            listErrors: [],
          },
        ],
        ...productWideValues,
      }
  }
}

// Get product title
export const getProductTitle = (product: string): React.ReactNode => {
  // Rna & Aqua are added temporarily for handling the compilation error for switch
  return (
    <FormattedMessage
      id={TubeTitleMap[product]?.id || product}
      defaultMessage={TubeTitleMap[product]?.defaultMessage || product}
      values={{
        registered: (
          <sup>
            <FormattedMessage
              id={TubeTitleMap[product]?.id || product}
              defaultMessage={TubeTitleMap[product]?.defaultMessage || product}
            />
          </sup>
        ),
      }}
    />
  )
}

// Get basic component based on product
export const getBaseComponent = (product: string): React.ReactElement => {
  // Rna & Aqua are added temporarily for handling the compilation error for switch
  switch (product) {
    case TubeProduct.Standard:
    case TubeProduct.Rna:
    case TubeProduct.Aqua:
    case TubeProduct.EasyOligo:
    case TubeProduct.PureSimple:
    case TubeProduct.iscaledna:
    case TubeProduct.LongOligo:
    case TubeProduct.Wellred:
    case TubeProduct.Beacon:
    case TubeProduct.Probe:
    case BiTubeProduct.LightCycler:
    case BiTubeProduct.FastDuplex:
    case BiTubeProduct.Duplex:
    case BiTubeProduct.mirna:
      return <DnaTubeLayout />
    default:
      return <></>
  }
}

export const getBaseLength = (product, country, probeType) => {
  return (
    productCountrySpecificBaseValue[product][probeType] ||
    productCountrySpecificBaseValue[product][country] ||
    productCountrySpecificBaseValue[product]['default']
  )
}
// Get product title
export const getProductSequenceInfoText = (
  product: string,
  country: string,
  probeType?: string
): React.ReactNode => {
  const baseLength = getBaseLength(product, country, probeType)
  const ValidLengthText = () => {
    return (
      <FormattedMessage
        id={`CONF_ENTER_SEQUENCE_PLACEHOLDER_${product}`}
        defaultMessage="Valid Length: {base} bases"
        values={{ base: `${baseLength.min} - ${baseLength.max}` }}
      />
    )
  }

  //Rna & Aqua are added temporarily for handling the compilation error for switch
  switch (product) {
    case TubeProduct.Standard:
    case TubeProduct.Aqua:
    case TubeProduct.Rna:
    case TubeProduct.iscaledna:
    case TubeProduct.LongOligo:
      return <ValidLengthText />
    case TubeProduct.Wellred:
    case TubeProduct.PureSimple:
    case TubeProduct.EasyOligo:
    case TubeProduct.Probe:
      return (
        <>
          <ValidLengthText />
          <br />
          <FormattedMessage
            id="CONF_SEQUENCE_MESSAGE_puresimple"
            defaultMessage="Standard DNA bases only: A, C, G, T"
          />
        </>
      )
    case TubeProduct.Beacon:
      return (
        <>
          <ValidLengthText />
          <br />
          <FormattedMessage
            id="CONF_SEQUENCE_MESSAGE_BEACON"
            defaultMessage="DNA bases only. Please be sure to include stem sequences."
          />
        </>
      )
    case BiTubeProduct.LightCycler:
      return <ValidLengthText />
    case BiTubeProduct.Duplex:
    case BiTubeProduct.FastDuplex:
      return (
        <>
          <ValidLengthText />,{' '}
          <FormattedMessage
            id="CONF_SEQUENCE_MESSAGE_DUPLEX"
            defaultMessage="including overhang | RNA bases only: A,C,G,U. T bases will be converted to U bases."
          />
        </>
      )
    case BiTubeProduct.mirna:
      return (
        <>
          <ValidLengthText /> |{' '}
          <FormattedMessage
            id="CONF_SEQUENCE_MESSAGE_mirna"
            defaultMessage="RNA bases only: A,C,G,U. T bases will be converted to U bases."
          />
        </>
      )
    default:
      return <></>
  }
}

//get Automated Asterisk insertion enabled status
export const getAsteriskInsertionStatus = (product: string) => {
  //Rna & Aqua are added temporarily for handling the compilation error for switch
  const { Standard, iscaledna, Rna, LongOligo } = TubeProduct
  const products: string[] = [Standard, iscaledna, Rna, LongOligo]
  return products.includes(product)
}

//determine if Modifications Component should be rendered
export const shouldRenderModifications = (product: string) => {
  const { Standard, Rna, Wellred, LongOligo, iscaledna, Probe, Beacon } =
    TubeProduct
  const products: string[] = [
    Standard,
    Rna,
    Wellred,
    LongOligo,
    iscaledna,
    Probe,
    Beacon,
  ]
  return products.includes(product)
}

export const shouldRenderApplyToNextOligo = (product: string) => {
  const { Standard, Rna, Wellred, LongOligo, iscaledna, Beacon, Probe } =
    TubeProduct
  const { LightCycler, Duplex, mirna, FastDuplex } = BiTubeProduct
  const products: string[] = [
    Standard,
    Rna,
    Wellred,
    LongOligo,
    iscaledna,
    Beacon,
    Probe,
    LightCycler,
    Duplex,
    mirna,
    FastDuplex,
  ]
  return products.includes(product)
}

export const shouldRenderSpecificationsHelpIcon = (product: string) => {
  const { Standard, Rna, Wellred, iscaledna, Probe } = TubeProduct
  const { LightCycler, Duplex, mirna, FastDuplex } = BiTubeProduct
  const products: string[] = [
    Standard,
    Rna,
    Wellred,
    iscaledna,
    Probe,
    LightCycler,
    Duplex,
    mirna,
    FastDuplex,
  ]
  return products.includes(product)
}

export const shouldRenderModificationCategory = (product: string) => {
  const { Standard } = TubeProduct
  const products: string[] = [Standard]
  return products.includes(product)
}

export const shouldSkipEnabledModificationsCheck = (product: string) => {
  const { LightCycler } = BiTubeProduct
  const products: string[] = [LightCycler]
  return products.includes(product)
}

export const shouldRenderBaseModificationMessage = (product: string) => {
  const { LightCycler } = BiTubeProduct
  const products: string[] = [LightCycler]
  return !products.includes(product)
}

export const shouldRenderFeasibilityMessage = (product: string) => {
  const { LightCycler, Duplex, mirna } = BiTubeProduct
  const products: string[] = [LightCycler, Duplex, mirna]
  return !products.includes(product)
}

export const shouldRenderTradeMarkHyperLink = (product: string) => {
  const { Standard, LongOligo, iscaledna, Beacon, Probe } = TubeProduct
  const products: string[] = [Standard, LongOligo, iscaledna, Beacon, Probe]
  return products.includes(product)
}

export const shouldExpandCommonAccordion = (product: string) => {
  const { Beacon, Wellred, Probe } = TubeProduct
  const products: string[] = [Beacon, Wellred, Probe]
  return products.includes(product)
}

export const readOnlySpecifications = (product: string) => {
  const { EasyOligo, PureSimple } = TubeProduct
  const products: string[] = [EasyOligo, PureSimple]
  return products.includes(product)
}

export const readOnlyPurification = (product: string) => {
  const { EasyOligo, PureSimple } = TubeProduct
  const products: string[] = [EasyOligo, PureSimple]
  return products.includes(product)
}

export const scaleRequiredForPurificationSelection = (product: string) => {
  const { Standard } = TubeProduct
  const products: string[] = [Standard]
  return products.includes(product)
}

export const shouldRenderIsoCheckBox = (product: string) => {
  const { Standard, Probe } = TubeProduct
  const products: string[] = [Standard, Probe]
  return products.includes(product)
}

export const shouldRenderRecommendationPopUp = (product: string) => {
  const { Standard, Rna } = TubeProduct
  const products: string[] = [Standard, Rna]
  return products.includes(product)
}

export const isRequiredAmountProduct = (product: string) => {
  const { Beacon, Probe } = TubeProduct
  const { LightCycler, Duplex, FastDuplex, mirna } = BiTubeProduct
  const products: string[] = [
    Beacon,
    Probe,
    LightCycler,
    Duplex,
    FastDuplex,
    mirna,
  ]
  return products.includes(product)
}
export const isRequiredPurificationProduct = (product: string) => {
  const { Standard } = TubeProduct
  const { Duplex, mirna } = BiTubeProduct
  const products: string[] = [Standard, Duplex, mirna]
  return products.includes(product)
}

export const isRequiredConcentrationProduct = (product: string) => {
  const { FastDuplex } = BiTubeProduct
  const products: string[] = [FastDuplex]
  return products.includes(product)
}

export const isBiTubeLightCyclerProduct = (product: string) => {
  const { LightCycler } = BiTubeProduct
  const products: string[] = [LightCycler]
  return products.includes(product)
}

export const isProbeProduct = (product: string) => {
  const { Beacon, Probe } = TubeProduct
  const products: string[] = [Beacon, Probe]
  return products.includes(product)
}

export const shouldRenderOverHangDropdown = (product: string) => {
  const { FastDuplex, Duplex } = BiTubeProduct
  const products: string[] = [FastDuplex, Duplex]
  return products.includes(product)
}

export const shouldRenderSingleNumberOfTubes = (product: string) => {
  const { FastDuplex } = BiTubeProduct
  const products: string[] = [FastDuplex]
  return products.includes(product)
}

export const shouldConvertTBasesToUBases = (product: string) => {
  const { FastDuplex, Duplex, mirna } = BiTubeProduct
  const products: string[] = [FastDuplex, Duplex, mirna]
  return products.includes(product)
}

export const shouldRenderAutoGenerate = (product: string) => {
  const { FastDuplex, Duplex, mirna } = BiTubeProduct
  const products: string[] = [FastDuplex, Duplex, mirna]
  return products.includes(product)
}

//TODO: Needs further addition based on the requirement
export const insertAsteriskBetweenCharacters = (
  text: string,
  hasFiveMod?: boolean,
  hasThreeMod?: boolean
) => {
  const nonAsterisk = text.trim().replace(/\*/g, '').split('')
  let formattedText = ''
  let isModification = false
  nonAsterisk.forEach((str, index) => {
    if (index === 0 && !!hasFiveMod) {
      if (str === '[') {
        isModification = true
        formattedText += `*${str}`
      } else formattedText += `*${str}*`
    } else if (
      index === nonAsterisk.length - 1 &&
      !!hasThreeMod &&
      !str.match(/\s/)
    ) {
      formattedText += `${str}*`
    } else if (
      str === '[' ||
      (isModification && str !== ']') ||
      index === nonAsterisk.length - 1 ||
      str.match(/\s/)
    ) {
      if (str === '[') isModification = true
      formattedText += str
    } else {
      if (str === ']') isModification = false
      formattedText += `${str}*`
    }
  })
  return formattedText
}

export const convertToUppercase = (text: string) => {
  let formattedText = ''
  let isModification = false
  let isPlusModification = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]

    if (char === '[') {
      isModification = true
      isPlusModification = text[i + 1] === '+'
      formattedText += char
    } else if (char === ']') {
      isModification = false
      isPlusModification = false
      formattedText += char
    } else if (isModification && !isPlusModification) {
      formattedText += char
    } else {
      formattedText += char.toUpperCase()
    }
  }

  return formattedText
}

//get product title
export const getProductBasedRequestMailId = (
  product: string,
  countryCode: string
): string => {
  if (TubeProduct.Standard) {
    if (countryCode === 'CA' || countryCode === 'US')
      return 'dnaoligos@milliporesigma.com'
    else return 'dnaoligos@merckgroup.com'
  } else {
    return 'dnaoligos@merckgroup.com'
  }
}

// Base Length Calculation
export const baseLengthValue = (base: string) => {
  if (!base) return 0
  return base
    .replace(/\*/g, '')
    .replace(/\s/g, '')
    .replace(/\[[^\]]*\]/g, '1').length
}

export const useRunAfterUpdate = () => {
  const afterPaintRef = React.useRef<Function | null>(null)
  if (typeof window !== 'undefined') {
    React.useLayoutEffect(() => {
      if (afterPaintRef.current) {
        afterPaintRef.current()
        afterPaintRef.current = null
      }
    })
  }
  return (fn: Function) => (afterPaintRef.current = fn)
}

export const getMaximumNumberOfOligosPerSession = (product: string): number => {
  switch (product) {
    case TubeProduct.Standard:
    case TubeProduct.Rna:
    case TubeProduct.Aqua:
    case TubeProduct.PureSimple:
    case TubeProduct.iscaledna:
    case TubeProduct.LongOligo:
    case TubeProduct.Wellred:
    case TubeProduct.Beacon:
    case TubeProduct.Probe:
      return 200
    case TubeProduct.EasyOligo:
      return 48
    default:
      return 200
  }
}

export const checkForRedesignedBiTubeProduct = (product: string): boolean => {
  const enabledBiTubeProducts: string[] = [
    BiTubeProduct.LightCycler,
    BiTubeProduct.Duplex,
    BiTubeProduct.FastDuplex,
    BiTubeProduct.mirna,
  ]
  return enabledBiTubeProducts.includes(product)
}

export const myOligoTitle = (product: string): React.ReactElement => {
  if (
    product === BiTubeProduct.FastDuplex ||
    product === BiTubeProduct.Duplex ||
    product === BiTubeProduct.mirna
  )
    return (
      <FormattedMessage
        id="CONF_MY_DUPLEXES_SIDE_BAR"
        defaultMessage="My Duplexes"
      />
    )
  else
    return (
      <FormattedMessage
        id="CONF_MY_OLIGOS_SIDE_BAR"
        defaultMessage="My Oligos"
      />
    )
}
export const myOligoBodyContent = (product: string): React.ReactElement => {
  const { FastDuplex, Duplex, mirna } = BiTubeProduct
  const products: string[] = [FastDuplex, Duplex, mirna]

  if (products.includes(product))
    return (
      <FormattedMessage
        id="CONF_LEFT_SIDE_BOTTOM_BAR_SUB_TITLE_DUPLEX"
        defaultMessage="Added Duplexes will appear here."
      />
    )
  else
    return (
      <FormattedMessage
        id="CONF_LEFT_SIDE_BOTTOM_BAR_SUB_TITLE"
        defaultMessage="Added Oligos will appear here."
      />
    )
}
export const addOligoLabel = (product: string): React.ReactElement => {
  const { FastDuplex, Duplex, mirna } = BiTubeProduct
  const products: string[] = [FastDuplex, Duplex, mirna]
  if (products.includes(product))
    return (
      <FormattedMessage id="CONF_ADD_DUPLEXS" defaultMessage="Add Duplex" />
    )
  else
    return <FormattedMessage id="CONF_ADD_OLIGOS" defaultMessage="Add Oligos" />
}

export enum SequenceType {
  Primary,
  Secondary,
}
export const biTubeSequenceTitle = (product: string, type: SequenceType) => {
  switch (product) {
    case BiTubeProduct.LightCycler:
    default:
      return type === SequenceType.Primary ? (
        <FormattedMessage
          id="DONOR_CONF_SEQUENCE_ENTRY"
          defaultMessage="Donor Sequence entry"
        />
      ) : (
        <FormattedMessage
          id="ACCEPTOR_CONF_SEQUENCE_ENTRY"
          defaultMessage="Acceptor Sequence entry"
        />
      )

    case BiTubeProduct.Duplex:
    case BiTubeProduct.FastDuplex:
      return type === SequenceType.Primary ? (
        <FormattedMessage
          id="DUPLEX_SENSE_STRAND_CONF_SEQUENCE_ENTRY"
          defaultMessage="Sense Strand Sequence entry"
        />
      ) : (
        <FormattedMessage
          id="DUPLEX_ANTISENSE_SEQUENCE_ENTRY"
          defaultMessage="Antisense Strand Sequence entry"
        />
      )
    case BiTubeProduct.mirna:
      return type === SequenceType.Primary ? (
        <FormattedMessage
          id="DUPLEX_ANTISENSE_SEQUENCE_ENTRY"
          defaultMessage="Antisense Strand Sequence entry"
        />
      ) : (
        <FormattedMessage
          id="DUPLEX_SENSE_STRAND_CONF_SEQUENCE_ENTRY"
          defaultMessage="Sense Strand Sequence entry"
        />
      )
  }
}

export const biTubeSequenceLabel = (product: string, type: SequenceType) => {
  switch (product) {
    case BiTubeProduct.LightCycler:
    default:
      return { id: 'SEQUENCE_UPDATED', defaultMessage: "Sequence (5' to 3')" }

    case BiTubeProduct.Duplex:
    case BiTubeProduct.FastDuplex:
      return type === SequenceType.Primary
        ? {
            id: 'CONF_DUPLEX_SENSE_OLIGO_SEQUENCE_UPDATED',
            defaultMessage: 'Sense Sequence (5’-3’)',
          }
        : {
            id: 'CONF_DUPLEX_ANTISENSE_OLIGO_SEQUENCE_UPDATED',
            defaultMessage: 'Antisense Sequence (5’-3’)',
          }
    case BiTubeProduct.mirna:
      return {
        id: 'CONF_DUPLEX_ANTISENSE_OLIGO_SEQUENCE_UPDATED',
        defaultMessage: 'Antisense Sequence (5’-3’)',
      }
  }
}

export const getProductBasedBaseType = (product: string): string => {
  if (product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna)
    return ModificationTypes.Base
  else return ModificationTypes.DegenerateBases
}

export const biTubeSequencePreviewName = (
  product: string,
  type: SequenceType
) => {
  switch (product) {
    case BiTubeProduct.LightCycler:
    default:
      return type === SequenceType.Primary ? (
        <FormattedMessage
          id="CONF_DONOR_lightcycler_sequence"
          defaultMessage="Donor Sequence"
        />
      ) : (
        <FormattedMessage
          id="CONF_ACCEPTOR_lightcycler_sequence"
          defaultMessage="Acceptor Sequence"
        />
      )

    case BiTubeProduct.Duplex:
    case BiTubeProduct.FastDuplex:
      return type === SequenceType.Primary ? (
        <FormattedMessage
          id="CONF_SENSE_SEQUENCE"
          defaultMessage="Sense Sequence"
        />
      ) : (
        <FormattedMessage
          id="CONF_ANTISENSE_SEQUENCE"
          defaultMessage="Antisense Sequence"
        />
      )
    case BiTubeProduct.mirna:
      return type === SequenceType.Primary ? (
        <FormattedMessage
          id="CONF_ANTISENSE_SEQUENCE_mirna"
          defaultMessage="Antisense (5’ to 3’)"
        />
      ) : (
        <FormattedMessage
          id="CONF_SENSE_SEQUENCE_mirna"
          defaultMessage="Sense (3’ to 5’)"
        />
      )
  }
}
export const biTubeSequencePropertiesTitle = (product: string) => {
  switch (product) {
    case BiTubeProduct.LightCycler:
    default:
      return (
        <FormattedMessage
          id="CONF_DNAPLATES_OLIGO_PROPERTIES"
          defaultMessage="Oligo Properties"
        />
      )

    case BiTubeProduct.Duplex:
    case BiTubeProduct.FastDuplex:
    case BiTubeProduct.mirna:
      return (
        <FormattedMessage
          id="CONF_OLIGO_NAME_FOR_DUPLEX_PROP"
          defaultMessage="Duplex Properties"
        />
      )
  }
}

export const biTubeSequencePropertiesNameTitle = (product: string) => {
  switch (product) {
    case BiTubeProduct.LightCycler:
    default:
      return (
        <FormattedMessage
          id="CONF_OLIGO_NAME_FOR_OLIGO_PROP"
          defaultMessage="Oligo Name"
        />
      )

    case BiTubeProduct.Duplex:
      return (
        <FormattedMessage
          id="CONF_OLIGO_NAME_STRAND_duplex"
          defaultMessage="Strand"
        />
      )
    case BiTubeProduct.FastDuplex:
    case BiTubeProduct.mirna:
      return (
        <FormattedMessage id="CONF_MIRNA_NAME_STRAND" defaultMessage="Strand" />
      )
  }
}

export const numberOfTubesPerOligoText = (product: string) => {
  switch (product) {
    default:
      return (
        <FormattedMessage
          id="_PC_SPEC_NOFT"
          defaultMessage="Number of Tubes per Oligo"
        />
      )
    case BiTubeProduct.Duplex:
    case BiTubeProduct.mirna:
      return (
        <FormattedMessage
          id="NUMBER_OF_TUBES_PER_DUPLEX"
          defaultMessage="Number of Tubes per Duplex"
        />
      )
    case BiTubeProduct.FastDuplex:
      return (
        <FormattedMessage
          id="NUMBER_OF_TUBES"
          defaultMessage="Number of Tubes"
        />
      )
  }
}

export const applyToNextLabel = (product: string) => {
  return product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna ? (
    <FormattedMessage
      id="APPLY_TO_NEXT_DUPLEX"
      defaultMessage="Apply to next Duplex"
    />
  ) : (
    <FormattedMessage
      id="CONF_APPLY_TO_NEXT_OLIGOS"
      defaultMessage="Apply to next Oligos"
    />
  )
}

export const hideGuaranteedBuild = (product: string) => {
  return product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna
}

export const getOligoNameLabel = (
  product: string,
  type: SequenceType
): React.ReactNode => {
  const nameLabelMap =
    type === SequenceType.Primary ? OligoNameMap : AntiSenseNameMap
  return (
    <FormattedMessage
      id={nameLabelMap[product]?.id || 'CONF_OLIGO_NAME_TUBE'}
      defaultMessage={nameLabelMap[product]?.defaultMessage || 'Oligo Name'}
    />
  )
}
export const displayStaticTitleInSequenceProperties = (product: string) => {
  return (
    product === BiTubeProduct.Duplex ||
    product === BiTubeProduct.mirna ||
    product === BiTubeProduct.FastDuplex
  )
}

const customCrisprGeneEditingObj = {
  id: 'CONF_CUSTOM_PRODUCT_NAME_CUSTOMCRISPR',
  defaultMessage: 'CRISPR & Gene Editing',
}

const productBreadcrumbLabels = {
  [TubeProduct.Standard]: {
    id: 'CONF_OLIGOS_PROBES_standard',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.Rna]: {
    id: 'CONF_OLIGOS_PROBES_rna',
    defaultMessage: 'RNA Oligos, Gene Knockout, Knockdown & Expression',
  },
  [TubeProduct.EasyOligo]: {
    id: 'CONF_OLIGOS_PROBES_easyoligo',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.PureSimple]: {
    id: 'CONF_OLIGOS_PROBES_standard',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.iscaledna]: {
    id: 'CONF_OLIGOS_PROBES_iscaledna',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.LongOligo]: {
    id: 'CONF_OLIGOS_PROBES_longoligo',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.Wellred]: {
    id: 'CONF_OLIGOS_PROBES_wellred',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.Beacon]: {
    id: 'CONF_OLIGOS_PROBES_wellred',
    defaultMessage: 'Oligos & Probes',
  },
  [TubeProduct.Probe]: {
    id: 'CONF_OLIGOS_PROBES_wellred',
    defaultMessage: 'Oligos & Probes',
  },
  [BiTubeProduct.LightCycler]: {
    id: 'CONF_OLIGOS_PROBES_lightcycle',
    defaultMessage: 'Oligos & Probes',
  },
  [BiTubeProduct.FastDuplex]: {
    id: 'CUSTOMOLIGO_GENE_KNOCKOUT',
    defaultMessage: 'Gene Knockout, Knockdown & Expression',
  },
  [BiTubeProduct.Duplex]: {
    id: 'CUSTOMOLIGO_GENE_KNOCKOUT',
    defaultMessage: 'Gene Knockout, Knockdown & Expression',
  },
  [BiTubeProduct.mirna]: {
    id: 'CUSTOMOLIGO_GENE_KNOCKOUT',
    defaultMessage: 'Gene Knockout, Knockdown & Expression',
  },
  [SemiConfigProductTypes.PREDESIGNED_CRISPR_GRNA]: customCrisprGeneEditingObj,
  [SemiConfigProductTypes.SANGERCRISPR]: customCrisprGeneEditingObj,
  [SemiConfigProductTypes.ORF]: {
    id: 'SEMICONF_BREADCRUMB2_shrna',
    defaultMessage: 'RNAi Single Clones',
  },
  [SemiConfigProductTypes.PREDESIGNED_SHRNA]: {
    id: 'SEMICONF_BREADCRUMB2_shrna',
    defaultMessage: 'RNAi Single Clones',
  },
}

export const getBreadCrumb = (product: string) => [
  {
    url: homeRoute.index(),
    label: <FormattedMessage id="HOME" defaultMessage="Home" />,
  },
  {
    url: BreadcrumbNavigationUrls.BreadcrumbCustomProducts,
    label: (
      <FormattedMessage id="CUSTOM_PRODUCTS" defaultMessage="Custom Products" />
    ),
  },
  ...(productBreadcrumbLabels[product]
    ? [
        {
          url: BreadcrumbNavigationUrls[`Breadcrumb_${product}`],
          label: (
            <FormattedMessage
              id={productBreadcrumbLabels[product].id}
              defaultMessage={productBreadcrumbLabels[product].defaultMessage}
            />
          ),
        },
      ]
    : []),
  {
    label: getProductTitle(product),
  },
]
